/*
  Gift Set picker.

  Holds the customer's accessory selection and works out which pick gets the
  free slot. The module never adds anything to the cart itself — the product
  form asks it for `getSelectedItems()` at submit time so the scooter and its
  gifts go up in a single /cart/add.js call. The confirm button only collapses
  the panel.

  The free slot goes to the highest compare-at price currently selected rather
  than to whatever was ticked first. Selection order then stops mattering: any
  combination lands on its best possible price, which removes a whole class of
  "I clicked them in the wrong order" support tickets.
*/

class GiftSet extends HTMLElement {
  connectedCallback() {
    if (this.dataset.giftSetReady === 'true') return;
    this.dataset.giftSetReady = 'true';

    this.freeQuota = parseInt(this.dataset.freeQuota, 10) || 1;
    // 0 means "no cap" — the metaobject field is left empty in that case.
    this.maxSelect = parseInt(this.dataset.maxSelect, 10) || 0;
    // Confirming is blocked until this many gifts are picked.
    this.minSelect = parseInt(this.dataset.minSelect, 10) || 1;

    this.openEl = this.querySelector('[data-gift-open]');
    // Remember the untouched CTA wording so it can come back if the customer
    // clears every pick after having confirmed once.
    this.chooseText = this.openEl ? this.openEl.textContent.trim() : '';
    this.editText = this.dataset.editText || this.chooseText;
    this.dialogEl = this.querySelector('[data-gift-dialog]');
    this.closeEl = this.querySelector('[data-gift-close]');
    this.confirmEl = this.querySelector('[data-gift-confirm]');
    this.pricesEl = this.querySelector('[data-gift-prices]');
    this.confirmHintEl = this.querySelector('[data-gift-confirm-hint]');
    this.confirmLabelEl = this.querySelector('[data-gift-confirm-label]');
    this.teaserThumbs = Array.prototype.slice.call(
      this.querySelectorAll('[data-teaser-thumb]')
    );
    this.confirmed = false;
    this.totalEl = this.querySelector('[data-gift-total]');
    this.totalWasEl = this.querySelector('[data-gift-total-was]');
    this.limitEl = this.querySelector('[data-gift-limit]');
    this.detailEl = this.querySelector('[data-gift-detail]');
    this.detailTitleEl = this.querySelector('[data-gift-detail-title]');
    this.detailBodyEl = this.querySelector('[data-gift-detail-body]');
    this.checks = Array.prototype.slice.call(this.querySelectorAll('[data-gift-check]'));

    if (!this.checks.length) return;

    // Signals that the script now owns which thumbnails are visible, releasing
    // the CSS-only fallback that caps the strip at three.
    this.classList.add('gift-set--ready');

    this.checks.forEach((check) => {
      check.addEventListener('change', () => this.render());
    });

    if (this.openEl) this.openEl.addEventListener('click', () => this.open());
    if (this.closeEl) this.closeEl.addEventListener('click', () => this.close());
    if (this.confirmEl) {
      this.confirmEl.addEventListener('click', () => {
        this.confirmed = true;
        this.close(true);
      });
    }

    this.wireDetails();

    if (this.dialogEl) {
      // Clicking the backdrop closes. The sheet is a child element, so a click
      // landing on the dialog itself can only have come from outside it.
      this.dialogEl.addEventListener('click', (event) => {
        if (event.target === this.dialogEl) this.close();
      });

      // Escape would close instantly and skip the exit animation, so take it
      // over and run the same path as every other dismissal.
      this.dialogEl.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    // Nothing is pre-selected. With an empty selection every card advertises
    // FREE, which is the honest state — any single pick really would be free.
    // Prices only appear once the free slot is spoken for.
    this.render();
  }

  /*
    The Details buttons and the shared dialog they drive.

    Each trigger lives inside its card's <label>, so its click must be stopped
    dead — left alone it would toggle the gift while opening the dialog, and the
    customer would have selected something just by reading about it.
  */
  wireDetails() {
    if (!this.detailEl) return;

    this.querySelectorAll('[data-gift-details]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openDetail(trigger);
      });
    });

    const closeBtn = this.querySelector('[data-gift-detail-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeDetail());

    this.detailEl.addEventListener('click', (event) => {
      if (event.target === this.detailEl) this.closeDetail();
    });

    // Escape closes only this dialog; the picker underneath stays open.
    this.detailEl.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDetail();
    });
  }

  openDetail(trigger) {
    if (this.detailTitleEl) this.detailTitleEl.textContent = trigger.dataset.giftTitle || '';
    // The description is rich text authored in Shopify admin, so it arrives as
    // markup and has to be written as HTML rather than as text.
    if (this.detailBodyEl) this.detailBodyEl.innerHTML = trigger.dataset.giftBody || '';
    this.lastDetailTrigger = trigger;
    this.detailEl.showModal();
    // Body scroll is already pinned by the picker, and must stay pinned when
    // this closes — so this dialog deliberately doesn't touch it.
  }

  closeDetail() {
    if (!this.detailEl || !this.detailEl.open) return;
    this.detailEl.close();
    if (this.lastDetailTrigger) this.lastDetailTrigger.focus();
  }

  compareOf(check) {
    return parseInt(check.dataset.comparePrice, 10) || 0;
  }

  paidOf(check) {
    return parseInt(check.dataset.paidPrice, 10) || 0;
  }

  /*
    A gift can only take the free slot if it ships a zero-priced tier. Costly
    accessories are stocked with a discounted tier alone, so they join the set
    at a reduced price without ever being eligible to be given away — which
    matters because the free slot otherwise goes to the highest retail price
    in the selection.
  */
  canBeFree(check) {
    return Boolean(check.dataset.freeId);
  }

  open() {
    if (!this.dialogEl) return;
    // Remember the state being edited so dismissing the panel can restore it.
    this.snapshot = this.checks.map((check) => check.checked);
    this.dialogEl.classList.remove('is-closing');
    this.dialogEl.showModal();
    // The page behind a modal dialog still scrolls on iOS; pinning the body is
    // the only reliable way to keep the sheet from dragging the page with it.
    document.body.style.overflow = 'hidden';
  }

  /*
    `commit` distinguishes confirming from dismissing. Anything other than the
    confirm button is a cancel and rolls the selection back, which is both the
    behaviour a modal implies and what keeps the minimum enforceable: without
    it, picking one gift and closing the panel would still send that gift to
    the cart, even though the confirm button refused it.
  */
  close(commit) {
    if (!this.dialogEl || !this.dialogEl.open) return;

    const finish = () => {
      // Roll back after the dialog is gone: doing it up front would show the
      // cards snapping back to their old state during the exit animation.
      if (!commit && this.snapshot) {
        this.checks.forEach((check, index) => {
          check.checked = this.snapshot[index];
        });
      }
      this.dialogEl.classList.remove('is-closing');
      this.dialogEl.close();
      document.body.style.overflow = '';
      this.render();
      if (this.openEl) this.openEl.focus();
    };

    this.dialogEl.classList.add('is-closing');

    // Let the exit animation play, but never hang the dialog open if the
    // animation was suppressed (reduced motion, unsupported, interrupted).
    const sheet = this.dialogEl.querySelector('.gift-set__sheet');
    if (!sheet) return finish();

    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      sheet.removeEventListener('animationend', once);
      finish();
    };
    sheet.addEventListener('animationend', once);
    setTimeout(once, 400);
  }

  /*
    Resolve the current selection into per-gift roles. Sorting by compare-at
    price descending means the free slots always land on the most valuable
    picks, whatever order the boxes were ticked in.
  */
  resolve() {
    const selected = this.checks.filter((check) => check.checked && !check.disabled);

    // Free slots are competed for only by the gifts that can actually be free.
    const contenders = selected
      .filter((check) => this.canBeFree(check))
      .sort((a, b) => this.compareOf(b) - this.compareOf(a));

    const roles = new Map();
    contenders.forEach((check, index) => {
      if (index < this.freeQuota) roles.set(check, 'free');
    });
    selected.forEach((check) => {
      if (!roles.has(check)) roles.set(check, 'paid');
    });

    // Ordered most valuable first, which is also the order gifts are listed in
    // the cart payload.
    const ranked = selected.slice().sort((a, b) => this.compareOf(b) - this.compareOf(a));

    let now = 0;
    let was = 0;
    ranked.forEach((check) => {
      now += roles.get(check) === 'free' ? 0 : this.paidOf(check);
      was += this.compareOf(check);
    });

    return { selected, ranked, roles, now, was };
  }

  /*
    Re-trigger the rise animation. Removing the class and forcing a reflow is
    what makes a second identical animation actually play.
  */
  bump(el) {
    if (!el) return;
    el.classList.remove('is-bumping');
    void el.offsetWidth;
    el.classList.add('is-bumping');
  }

  // Write markup only when it changed, so unrelated clicks don't make every
  // number in the sheet twitch. The first paint is silent: animating a value
  // that was already on screen at page load reads as a glitch, not a change.
  setHTML(el, html) {
    if (!el || el.dataset.shown === html) return;
    const isFirstPaint = el.dataset.shown === undefined;
    el.dataset.shown = html;
    el.innerHTML = html;
    if (!isFirstPaint) this.bump(el);
  }

  // Show or hide a label, animating it only on the transition into view.
  toggleLabel(el, visible) {
    if (!el || el.hidden === !visible) return;
    el.hidden = !visible;
    if (visible) this.bump(el);
  }

  money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.moneyFormat);
    }
    // Fall back to a bare number rather than rendering "undefined" if the
    // money helper hasn't loaded yet.
    return (cents / 100).toFixed(2);
  }

  render() {
    const state = this.resolve();

    this.checks.forEach((check) => {
      const role = state.roles.get(check);
      const freeLabel = check.parentElement.querySelector('[data-gift-free-label]');
      const paidLabel = check.parentElement.querySelector('[data-gift-paid-label]');

      // Before anything is picked, the free slot is still up for grabs, so the
      // gifts that could win it show FREE. Discount-only gifts always show
      // their price — promising them free would be a lie we'd have to retract.
      const showFree =
        role === 'free' || (state.selected.length === 0 && this.canBeFree(check));
      this.toggleLabel(freeLabel, showFree);
      this.toggleLabel(paidLabel, !showFree);
      if (paidLabel && !showFree) {
        // shop.money_format carries markup (e.g. <span class=money>), so this
        // has to be assigned as HTML — textContent renders the tags as text.
        this.setHTML(paidLabel, '+' + this.money(this.paidOf(check)));
      }
    });

    // Cap enforcement: once the limit is hit, the remaining cards go inert
    // instead of silently ignoring clicks.
    if (this.maxSelect > 0) {
      const atCap = state.selected.length >= this.maxSelect;
      this.checks.forEach((check) => {
        check.disabled = atCap && !check.checked;
      });
    }

    const picked = state.selected.length;
    const hasPicks = picked > 0;
    const minimum = Math.max(1, this.minSelect);
    const hasEnough = picked >= minimum;

    // Confirming waits for the minimum, but the running total appears from the
    // very first pick — the price should never be a surprise at the end.
    if (this.confirmEl) this.confirmEl.disabled = !hasEnough;
    // The total only shows once it belongs to a set that can be confirmed;
    // below the minimum the button is given over to the instruction.
    if (this.pricesEl) this.pricesEl.hidden = !hasEnough;
    if (this.confirmLabelEl) this.confirmLabelEl.hidden = !hasEnough;

    if (hasEnough) {
      this.setHTML(this.totalEl, this.money(state.now));
      const showWas = state.was > state.now;
      this.toggleLabel(this.totalWasEl, showWas);
      if (showWas) this.setHTML(this.totalWasEl, this.money(state.was));
    }

    this.syncTeaser(state);
    this.setHint(picked, minimum, hasEnough);
    this.syncCta(hasPicks);

    this.dispatchEvent(
      new CustomEvent('gift-set:change', {
        bubbles: true,
        detail: { count: state.selected.length, total: state.now }
      })
    );
  }

  /*
    Guidance splits between two places by which one the customer is looking at.
    Short of the minimum, the instruction takes over the confirm button, where
    the eye already is when trying to proceed. Once the set is valid, only the
    cap notice remains, and that belongs under the title with the cards.
  */
  setHint(picked, minimum, hasEnough) {
    if (this.confirmHintEl) {
      if (!hasEnough) {
        const need = minimum - picked;
        this.setHTML(
          this.confirmHintEl,
          need === 1
            ? 'Choose 1 more gift to continue.'
            : 'Choose ' + need + ' more gifts to continue.'
        );
      }
      this.confirmHintEl.hidden = hasEnough;
    }

    if (!this.limitEl) return;

    let text = '';
    if (hasEnough && this.maxSelect > 0 && picked >= this.maxSelect) {
      text = 'You can pick up to ' + this.maxSelect + ' gifts.';
    }

    if (!text) {
      // Keep the old wording during the collapse; swapping it to empty first
      // would make the line blank out before it finished shrinking.
      this.limitEl.classList.remove('is-visible');
      return;
    }

    const changed = this.limitEl.textContent !== text;
    if (changed) this.limitEl.textContent = text;

    const wasVisible = this.limitEl.classList.contains('is-visible');
    this.limitEl.classList.add('is-visible');

    // Nudge only when the message changed while already on screen; on the way
    // in, the expand transition is the animation.
    if (changed && wasVisible) this.bump(this.limitEl);
  }

  /*
    The strip's thumbnails. Before a set is confirmed they advertise what is on
    offer; afterwards they show what was actually chosen, so the collapsed strip
    reflects the customer's own selection.

    Position-dependent styling (the fan and the overlap) is applied here rather
    than in CSS, because `:nth-child` counts hidden thumbnails too and would
    fan the wrong ones once the list is filtered.
  */
  syncTeaser(state) {
    if (!this.teaserThumbs.length) return;

    const chosen =
      this.confirmed && state.selected.length
        ? new Set(state.selected.map((check) => check.dataset.paidId))
        : null;

    const angles = ['rotate(-7deg)', 'none', 'rotate(7deg)'];
    let shown = 0;

    this.teaserThumbs.forEach((thumb) => {
      const wanted = chosen ? chosen.has(thumb.dataset.giftId) : true;
      const show = wanted && shown < 3;
      thumb.hidden = !show;

      if (show) {
        thumb.style.marginLeft = shown === 0 ? '0' : '';
        thumb.style.transform = angles[Math.min(shown, angles.length - 1)];
        shown += 1;
      }
    });
  }

  /*
    Once a set has been confirmed the strip offers to revise it rather than to
    start over. Driven by the live selection, not by a "has confirmed once"
    flag, so clearing every pick puts the original wording back.
  */
  syncCta(hasPicks) {
    if (!this.openEl) return;
    const wanted = hasPicks && this.confirmed ? this.editText : this.chooseText;
    if (this.openEl.textContent.trim() === wanted) return;
    this.openEl.textContent = wanted;
    this.bump(this.openEl);
  }

  /*
    Line items for the cart, one per selected gift.

    Two back-references are written, because they answer different questions.
    `_gift_for` names the exact scooter variant, which is what the warehouse
    reads to pick the model-specific version of a generic gift SKU.
    `_gift_for_product` names the product, and is what the cart guard matches
    on — swapping the scooter's colour shouldn't strip the gifts off the cart,
    but removing the scooter entirely should.

    Quantity is always 1. Gifts are per order, not per scooter: three scooters
    in one cart still get one giveaway.
  */
  getSelectedItems(mainVariantId) {
    const state = this.resolve();
    const mainProductId = this.dataset.mainProductId || '';

    return state.ranked.map((check) => {
      const role = state.roles.get(check);
      return {
        id: parseInt(role === 'free' ? check.dataset.freeId : check.dataset.paidId, 10),
        quantity: 1,
        properties: {
          _gift_for: String(mainVariantId || ''),
          _gift_for_product: String(mainProductId),
          _gift_role: role,
          _gift_name: check.dataset.giftName || ''
        }
      };
    });
  }
}

if (!customElements.get('gift-set')) {
  customElements.define('gift-set', GiftSet);
}
